import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  fetchProperties,
  Mls2ApiError,
  type FetchPropertiesParams,
  type PaginatedProperties,
} from "../lib/api";

const PAGE_SIZE = 20;

interface FilterForm {
  city: string;
  neighborhood: string;
  rooms: string;
  minPrice: string;
  maxPrice: string;
}

const EMPTY_FILTERS: FilterForm = {
  city: "",
  neighborhood: "",
  rooms: "",
  minPrice: "",
  maxPrice: "",
};

export function Properties() {
  const [form, setForm] = useState<FilterForm>(EMPTY_FILTERS);
  const [filters, setFilters] = useState<FetchPropertiesParams>({});
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<PaginatedProperties | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    let isCurrent = true;

    async function loadProperties() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchProperties({
          ...filters,
          page,
          pageSize: PAGE_SIZE,
        });
        if (isCurrent) setResult(response);
      } catch (requestError) {
        if (isCurrent) {
          setError(
            requestError instanceof Mls2ApiError
              ? requestError.message
              : "Unable to load properties.",
          );
        }
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    }

    void loadProperties();
    return () => {
      isCurrent = false;
    };
  }, [filters, page, requestVersion]);

  function applyFilters(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPage(1);
    setFilters({
      city: optionalText(form.city),
      neighborhood: optionalText(form.neighborhood),
      rooms: optionalNumber(form.rooms),
      minPrice: optionalNumber(form.minPrice),
      maxPrice: optionalNumber(form.maxPrice),
    });
  }

  function clearFilters() {
    setForm(EMPTY_FILTERS);
    setFilters({});
    setPage(1);
  }

  const pagination = result?.pagination;

  return (
    <section className="mx-auto max-w-7xl">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-mls-600">
            Canonical inventory
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
            Properties
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Browse deduplicated public and broker-contributed property facts.
          </p>
        </div>
        <p className="text-sm text-slate-500">
          {pagination ? pagination.total.toLocaleString() + " records" : "Loading inventory…"}
        </p>
      </header>

      <form
        className="mt-8 grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:grid-cols-2 lg:grid-cols-5"
        onSubmit={applyFilters}
      >
        <FilterField
          label="City"
          value={form.city}
          onChange={(value) => setForm((current) => ({ ...current, city: value }))}
        />
        <FilterField
          label="Neighborhood"
          value={form.neighborhood}
          onChange={(value) =>
            setForm((current) => ({ ...current, neighborhood: value }))
          }
        />
        <FilterField
          label="Rooms"
          value={form.rooms}
          type="number"
          min="0"
          step="0.5"
          onChange={(value) => setForm((current) => ({ ...current, rooms: value }))}
        />
        <FilterField
          label="Minimum price"
          value={form.minPrice}
          type="number"
          min="0"
          onChange={(value) =>
            setForm((current) => ({ ...current, minPrice: value }))
          }
        />
        <FilterField
          label="Maximum price"
          value={form.maxPrice}
          type="number"
          min="0"
          onChange={(value) =>
            setForm((current) => ({ ...current, maxPrice: value }))
          }
        />

        <div className="flex gap-3 sm:col-span-2 lg:col-span-5">
          <button
            className="rounded-lg bg-mls-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-mls-600"
            type="submit"
          >
            Apply filters
          </button>
          <button
            className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            type="button"
            onClick={clearFilters}
          >
            Clear
          </button>
        </div>
      </form>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {error ? (
          <div className="p-8 text-center" role="alert">
            <p className="font-medium text-red-700">{error}</p>
            <button
              className="mt-4 text-sm font-semibold text-mls-600 hover:underline"
              type="button"
              onClick={() => setRequestVersion((current) => current + 1)}
            >
              Try again
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <Column>Location</Column>
                  <Column>Price</Column>
                  <Column>Rooms</Column>
                  <Column>Area</Column>
                  <Column>Freshness</Column>
                  <th className="px-5 py-4 text-right font-semibold" scope="col">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  <LoadingRows />
                ) : result?.data.length ? (
                  result.data.map((property) => (
                    <tr className="transition hover:bg-mls-50" key={property.id}>
                      <td className="px-5 py-4">
                        <p className="font-medium text-slate-950">
                          {property.neighborhood ?? "Unknown neighborhood"}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {property.city ?? "Unknown city"}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 font-medium text-slate-900">
                        {formatPrice(property.price)}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-slate-600">
                        {property.rooms ?? "—"}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-slate-600">
                        {property.sqm ? property.sqm.toLocaleString() + " m²" : "—"}
                      </td>
                      <td className="px-5 py-4">
                        <Freshness value={property.freshness_score} />
                      </td>
                      <td className="px-5 py-4 text-right">
                        <Link
                          className="font-semibold text-mls-600 hover:underline"
                          to={"/properties/" + encodeURIComponent(property.id)}
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="px-5 py-12 text-center text-slate-500" colSpan={6}>
                      No properties match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination && pagination.totalPages > 1 ? (
        <nav
          className="mt-6 flex items-center justify-between"
          aria-label="Property pagination"
        >
          <button
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold disabled:opacity-40"
            type="button"
            disabled={page <= 1 || isLoading}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </button>
          <p className="text-sm text-slate-600">
            Page <strong>{pagination.page}</strong> of{" "}
            <strong>{pagination.totalPages}</strong>
          </p>
          <button
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold disabled:opacity-40"
            type="button"
            disabled={page >= pagination.totalPages || isLoading}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </button>
        </nav>
      ) : null}
    </section>
  );
}

interface FilterFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "number";
  min?: string;
  step?: string;
}

function FilterField({
  label,
  value,
  onChange,
  type = "text",
  min,
  step,
}: FilterFieldProps) {
  return (
    <label className="text-sm font-medium text-slate-700">
      {label}
      <input
        className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 outline-none transition focus:border-mls-600 focus:ring-2 focus:ring-mls-600/20"
        type={type}
        min={min}
        step={step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function Column({ children }: { children: string }) {
  return (
    <th className="px-5 py-4 font-semibold" scope="col">
      {children}
    </th>
  );
}

function Freshness({ value }: { value: number }) {
  const color =
    value >= 70
      ? "bg-emerald-50 text-emerald-700"
      : value >= 40
        ? "bg-amber-50 text-amber-700"
        : "bg-slate-100 text-slate-600";

  return (
    <span
      className={
        "inline-flex rounded-full px-2.5 py-1 text-xs font-semibold " + color
      }
    >
      {value}/100
    </span>
  );
}

function LoadingRows() {
  return Array.from({ length: 5 }, (_, index) => (
    <tr className="animate-pulse" key={index}>
      <td className="px-5 py-5" colSpan={6}>
        <div className="h-4 rounded bg-slate-100" />
      </td>
    </tr>
  ));
}

function optionalText(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed || undefined;
}

function optionalNumber(value: string): number | undefined {
  if (!value) return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function formatPrice(price: number | null): string {
  return price === null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      }).format(price);
}